import {
  domainRouterClient,
  type DomainRouterMutationArgs,
  type DomainRouterMutationData,
  type DomainRouterMutationName,
} from './domainRouterClient';

type DomainMutationClientName =
  | 'updateUserPreferences'
  | 'requestFinancialAidHistoricalImportUpload'
  | 'startFinancialAidHistoricalImport'
  | 'markNotificationRead'
  | 'markAllNotificationsRead'
  | 'updateNotificationPreferences'
  | 'sendMessage'
  | 'markMessageRead'
  | 'createFamilyBillingEntry'
  | 'createFinancialAidApplication'
  | 'updateFinancialAidApplication'
  | 'submitFinancialAidApplication'
  | 'uploadFinancialAidDocument'
  | 'confirmFinancialAidDocumentUpload'
  | 'acceptFinancialAidAward'
  | 'declineFinancialAidAward'
  | 'respondToFinancialAidInfoRequest'
  | 'createFinancialAidAppeal'
  | 'beginFinancialAidReview'
  | 'makeFinancialAidDecision'
  | 'resolveFinancialAidFlag'
  | 'requestFinancialAidMoreInfo'
  | 'updateFinancialAidDocumentChecklist'
  | 'makeFinancialAidAppealDecision'
  | 'exportFinancialAidReport'
  | 'generateFinancialAidApplicationReport'
  | 'getFinancialAidApplicationReportDownloadUrl'
  | 'generateFinancialAidDecisionLetter';

type MutationResult<Name extends DomainMutationClientName> = DomainRouterMutationData<Name>;

type JsonInputMutationName = DomainMutationClientName;

type JsonMutationInput<Name extends JsonInputMutationName> =
  DomainRouterMutationArgs<Name> extends { input: infer Input }
    ? Input
    : DomainRouterMutationArgs<Name>;

const toAwsJson = <T>(value: T): string | T => (typeof value === 'string' ? value : JSON.stringify(value));
type UntypedMutationRunner = (name: DomainRouterMutationName, args: unknown) => Promise<unknown>;
const runUntypedMutation = domainRouterClient.mutate as unknown as UntypedMutationRunner;

export const domainMutationClient = {
  updateUserPreferences(input: JsonMutationInput<'updateUserPreferences'>) {
    return runUntypedMutation('updateUserPreferences', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'updateUserPreferences'>>;
  },
  requestFinancialAidHistoricalImportUpload(input: JsonMutationInput<'requestFinancialAidHistoricalImportUpload'>) {
    return runUntypedMutation('requestFinancialAidHistoricalImportUpload', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'requestFinancialAidHistoricalImportUpload'>>;
  },
  startFinancialAidHistoricalImport(input: JsonMutationInput<'startFinancialAidHistoricalImport'>) {
    return runUntypedMutation('startFinancialAidHistoricalImport', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'startFinancialAidHistoricalImport'>>;
  },
  markNotificationRead(input: JsonMutationInput<'markNotificationRead'>) {
    return runUntypedMutation('markNotificationRead', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'markNotificationRead'>>;
  },
  markAllNotificationsRead(input: JsonMutationInput<'markAllNotificationsRead'>) {
    return runUntypedMutation('markAllNotificationsRead', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'markAllNotificationsRead'>>;
  },
  updateNotificationPreferences(input: JsonMutationInput<'updateNotificationPreferences'>) {
    return runUntypedMutation('updateNotificationPreferences', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'updateNotificationPreferences'>>;
  },
  sendMessage(input: JsonMutationInput<'sendMessage'>) {
    return runUntypedMutation('sendMessage', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'sendMessage'>>;
  },
  markMessageRead(input: JsonMutationInput<'markMessageRead'>) {
    return runUntypedMutation('markMessageRead', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'markMessageRead'>>;
  },
  createFamilyBillingEntry(input: JsonMutationInput<'createFamilyBillingEntry'>) {
    return runUntypedMutation('createFamilyBillingEntry', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'createFamilyBillingEntry'>>;
  },
  createFinancialAidApplication(input: JsonMutationInput<'createFinancialAidApplication'>) {
    return runUntypedMutation('createFinancialAidApplication', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'createFinancialAidApplication'>>;
  },
  updateFinancialAidApplication(input: JsonMutationInput<'updateFinancialAidApplication'>) {
    return runUntypedMutation('updateFinancialAidApplication', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'updateFinancialAidApplication'>>;
  },
  submitFinancialAidApplication(input: JsonMutationInput<'submitFinancialAidApplication'>) {
    return runUntypedMutation('submitFinancialAidApplication', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'submitFinancialAidApplication'>>;
  },
  uploadFinancialAidDocument(input: JsonMutationInput<'uploadFinancialAidDocument'>) {
    return runUntypedMutation('uploadFinancialAidDocument', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'uploadFinancialAidDocument'>>;
  },
  confirmFinancialAidDocumentUpload(input: JsonMutationInput<'confirmFinancialAidDocumentUpload'>) {
    return runUntypedMutation('confirmFinancialAidDocumentUpload', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'confirmFinancialAidDocumentUpload'>>;
  },
  acceptFinancialAidAward(input: JsonMutationInput<'acceptFinancialAidAward'>) {
    return runUntypedMutation('acceptFinancialAidAward', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'acceptFinancialAidAward'>>;
  },
  declineFinancialAidAward(input: JsonMutationInput<'declineFinancialAidAward'>) {
    return runUntypedMutation('declineFinancialAidAward', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'declineFinancialAidAward'>>;
  },
  respondToFinancialAidInfoRequest(input: JsonMutationInput<'respondToFinancialAidInfoRequest'>) {
    return runUntypedMutation('respondToFinancialAidInfoRequest', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'respondToFinancialAidInfoRequest'>>;
  },
  createFinancialAidAppeal(input: JsonMutationInput<'createFinancialAidAppeal'>) {
    return runUntypedMutation('createFinancialAidAppeal', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'createFinancialAidAppeal'>>;
  },
  beginFinancialAidReview(input: JsonMutationInput<'beginFinancialAidReview'>) {
    return runUntypedMutation('beginFinancialAidReview', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'beginFinancialAidReview'>>;
  },
  makeFinancialAidDecision(input: JsonMutationInput<'makeFinancialAidDecision'>) {
    return runUntypedMutation('makeFinancialAidDecision', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'makeFinancialAidDecision'>>;
  },
  resolveFinancialAidFlag(input: JsonMutationInput<'resolveFinancialAidFlag'>) {
    return runUntypedMutation('resolveFinancialAidFlag', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'resolveFinancialAidFlag'>>;
  },
  requestFinancialAidMoreInfo(input: JsonMutationInput<'requestFinancialAidMoreInfo'>) {
    return runUntypedMutation('requestFinancialAidMoreInfo', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'requestFinancialAidMoreInfo'>>;
  },
  updateFinancialAidDocumentChecklist(input: JsonMutationInput<'updateFinancialAidDocumentChecklist'>) {
    return runUntypedMutation('updateFinancialAidDocumentChecklist', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'updateFinancialAidDocumentChecklist'>>;
  },
  makeFinancialAidAppealDecision(input: JsonMutationInput<'makeFinancialAidAppealDecision'>) {
    return runUntypedMutation('makeFinancialAidAppealDecision', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'makeFinancialAidAppealDecision'>>;
  },
  exportFinancialAidReport(input: JsonMutationInput<'exportFinancialAidReport'>) {
    return runUntypedMutation('exportFinancialAidReport', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'exportFinancialAidReport'>>;
  },
  generateFinancialAidApplicationReport(input: JsonMutationInput<'generateFinancialAidApplicationReport'>) {
    return runUntypedMutation('generateFinancialAidApplicationReport', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'generateFinancialAidApplicationReport'>>;
  },
  getFinancialAidApplicationReportDownloadUrl(input: JsonMutationInput<'getFinancialAidApplicationReportDownloadUrl'>) {
    return runUntypedMutation('getFinancialAidApplicationReportDownloadUrl', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'getFinancialAidApplicationReportDownloadUrl'>>;
  },
  generateFinancialAidDecisionLetter(input: JsonMutationInput<'generateFinancialAidDecisionLetter'>) {
    return runUntypedMutation('generateFinancialAidDecisionLetter', {
      input: toAwsJson(input),
    }) as Promise<MutationResult<'generateFinancialAidDecisionLetter'>>;
  },
};
